import { useLayoutEffect } from 'react';

interface UseSyncHeightOptions {
  /**
   * 选择器，用于选择需要同步高度的元素
   * @default '.sync-height'
   */
  selector?: string;
  /**
   * 是否在窗口大小变化时重新计算高度
   * @default true
   */
  syncOnResize?: boolean;
  /**
   * 是否在元素内容变化时重新计算高度
   * @default true
   */
  syncOnContentChange?: boolean;
}

/**
 * 同步多个元素高度的 Hook
 * @param options 配置选项
 */
export function useSyncHeight(options: UseSyncHeightOptions = {}) {
  const { selector = '.sync-height', syncOnResize = true, syncOnContentChange = true } = options;

  useLayoutEffect(() => {
    const setMaxHeight = () => {
      const elements = document.querySelectorAll<HTMLElement>(selector);
      if (elements.length > 0) {
        for (const el of elements) el.style.minHeight = '';
        const maxHeight = Math.max(...Array.from(elements).map((el) => el.scrollHeight));
        for (const el of elements) el.style.minHeight = `${maxHeight}px`;
      }
    };

    // 初始设置高度
    setMaxHeight();

    // 监听窗口大小变化
    if (syncOnResize) {
      window.addEventListener('resize', setMaxHeight);
    }

    // 监听内容变化
    if (syncOnContentChange) {
      const observer = new ResizeObserver(setMaxHeight);
      const elements = document.querySelectorAll(selector);
      // biome-ignore lint/suspicious/useIterableCallbackReturn: observe each matched element in place
      elements.forEach((el) => observer.observe(el));

      return () => {
        window.removeEventListener('resize', setMaxHeight);
        observer.disconnect();
      };
    }

    return () => {
      window.removeEventListener('resize', setMaxHeight);
    };
  }, [selector, syncOnResize, syncOnContentChange]);
}
